import { mergeAttributes, Node } from '@tiptap/core';
import { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { humanFileSize } from '@/components/tiptap/utils/shared';
import moment from 'moment';
import apiService from '@/services/ApiService';
import workspaceService from '@/services/workspace/WorkspaceService';
export type FileAttachmentOptions = {
  HTMLAttributes: Record<string, any>;
  renderTitle: (node: ProseMirrorNode) => string;
  renderDetails: (node: ProseMirrorNode) => string;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    fileAttachment: {
      insertFileAttachment: (options: {
        id: string;
        url: string;
        name: string;
        ext: string;
        time: number;
        size?: number;
      }) => ReturnType;
    };
  }
}

const FileAttachment = Node.create<FileAttachmentOptions>({
  name: 'fileAttachment',

  addOptions() {
    return {
      HTMLAttributes: {},
      renderTitle(node) {
        return `${node.attrs.name}.${node.attrs.ext}`;
      },
      renderDetails(node) {
        const time = moment(node.attrs.time);
        return `${time.format('DD MMM YYYY, HH:mm')}${
          node.attrs.size ? ' | ' + humanFileSize(node.attrs.size, true) : ''
        }`;
      },
    };
  },

  group: 'inline',
  inline: true,
  selectable: false,
  draggable: true,
  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-id'),
        renderHTML: (attributes) => (attributes.id ? { 'data-id': attributes.id } : {}),
      },
      url: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-url'),
        renderHTML: (attributes) => (attributes.url ? { 'data-url': attributes.url } : {}),
      },
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-name'),
        renderHTML: (attributes) => (attributes.name ? { 'data-name': attributes.name } : {}),
      },
      ext: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-ext'),
        renderHTML: (attributes) => (attributes.ext ? { 'data-ext': attributes.ext } : {}),
      },
      size: {
        default: null,
        parseHTML: (element) => parseInt(element.getAttribute('data-size')),
        renderHTML: (attributes) => (attributes.size ? { 'data-size': attributes.size } : {}),
      },
      time: {
        default: null,
        parseHTML: (element) => parseInt(element.getAttribute('data-time')),
        renderHTML: (attributes) => (attributes.time ? { 'data-time': attributes.time } : {}),
      },
      diff: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-diff'),
        renderHTML: (attributes) => (attributes.diff ? { 'data-diff': attributes.diff } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: `div[data-type="${this.name}"]` }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(
        {
          'data-type': this.name,
          class: 'tiptap-file-attachment',
        },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      [
        'a',
        { class: 'tiptap-file-attachment__link', href: node.attrs.url, target: '_blank' },
        ['div', { class: 'tiptap-file-attachment__ext' }, node.attrs.ext],
        ['div', { class: 'tiptap-file-attachment__title' }, this.options.renderTitle(node)],
        ['div', { class: 'tiptap-file-attachment__details' }, this.options.renderDetails(node)],
      ],
    ];
  },

  renderText({ node }) {
    return this.options.renderTitle(node);
  },

  addCommands() {
    return {
      insertFileAttachment:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs: options });
        },
    };
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.classList.add('tiptap-file-attachment');
      dom.dataset.type = this.name;

      const link = document.createElement('a');
      link.className = 'tiptap-file-attachment__link';
      link.target = '_blank';
      link.href = node.attrs.url || '#';

      const extDiv = document.createElement('div');
      extDiv.className = 'tiptap-file-attachment__ext';
      extDiv.textContent = node.attrs.ext;

      const titleDiv = document.createElement('div');
      titleDiv.className = 'tiptap-file-attachment__title';
      titleDiv.textContent = this.options.renderTitle(node);

      const detailsDiv = document.createElement('div');
      detailsDiv.className = 'tiptap-file-attachment__details';
      detailsDiv.textContent = this.options.renderDetails(node);

      link.append(extDiv, titleDiv, detailsDiv);
      dom.appendChild(link);

      // Apply diff border styling (data-diff set by backend promoteFileAttachmentDiffs)
      if (node.attrs.diff === 'ins') {
        link.style.border = '3px solid rgba(46, 160, 67, 0.6)';
        link.style.borderRadius = '4px';
      } else if (node.attrs.diff === 'del' || node.attrs.diff === 'mod') {
        link.style.border = '3px solid rgba(248, 81, 73, 0.6)';
        link.style.borderRadius = '4px';
      }

      // Async API call to update URL (skip if url is null, e.g. in diff mode)
      if (node.attrs.url) {
        (async () => {
          try {
            const src = node.attrs.url.split('?')[0];
            const match = src.match(/file-attachments\/.+$/);
            if (!match) {
              // eslint-disable-next-line no-console
              console.error('Path not found');
              return;
            }
            const path = match[0];

            const isSharedMode = () => {
              return workspaceService.workspaceView.isSharedMode;
            };
            const res = !isSharedMode()
              ? await apiService.fileAttachment.getUrl(path)
              : await apiService.browse.getSharedFileAttachmentUrl(path, workspaceService.workspaceView.sharedToken);

            if (!res?.data?.signedUrl) {
              // eslint-disable-next-line no-console
              console.error('Opps somthing went wrong');
              return;
            }
            const signedUrl = res.data.signedUrl;

            // Update DOM
            link.href = signedUrl;
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch file URL:', err);
          }
        })();
      }

      return { dom };
    };
  },
});

export default FileAttachment;
